<template>
  <div
    :class="[
      style.row,
      isDragging ? style.dragging : '',
      isDragOver ? style.dragOver : ''
    ]"
    :draggable="dragEnabled && !disabled"
    @dragstart="onDragStart"
    @dragend="onDragEnd"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <div :class="style.content">
      <InputNamedValue
        :name="name"
        :value="value"
        :name-placeholder="namePlaceholder"
        :value-placeholder="valuePlaceholder"
        :suffix="suffix"
        :disabled="disabled"
        :readonly="readonly"
        :error="error"
        :value-input-mode="valueInputMode"
        @drag-start="enableDrag"
        @update:name="$emit('update:name', $event)"
        @update:value="$emit('update:value', $event)"
        @change:name="$emit('change:name', $event)"
        @change:value="$emit('change:value', $event)"
      />
    </div>

    <div v-if="$slots.actions" :class="style.actions">
      <slot name="actions" />
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent} from "vue";
import style from "./DragDropItem.module.scss";
import InputNamedValue from "@/elements/UI/input/NamedValue/InputNamedValue.vue";

export default defineComponent({
  name: "DragDropItemBase",
  components: {
    InputNamedValue
  },
  emits: [
    "drag-start",
    "drag-end",
    "drop-item",
    "update:name",
    "update:value",
    "change:name",
    "change:value"
  ],
  props: {
    itemId: {
      type: [String, Number],
      required: true
    },
    name: {
      type: String,
      default: ""
    },
    value: {
      type: String,
      default: ""
    },
    namePlaceholder: {
      type: String,
      default: "Текст"
    },
    valuePlaceholder: {
      type: String,
      default: ""
    },
    suffix: {
      type: String,
      default: "₽"
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    valueInputMode: {
      type: String,
      default: "text"
    }
  },
  data() {
    return {
      style,
      dragEnabled: false,
      isDragging: false,
      isDragOver: false
    };
  },
  beforeUnmount() {
    window.removeEventListener("pointerup", this.onWindowPointerUp);
  },
  methods: {
    enableDrag(event: PointerEvent): void {
      if (this.disabled || event.button !== 0) {
        return;
      }

      this.dragEnabled = true;
      window.addEventListener("pointerup", this.onWindowPointerUp, {
        once: true
      });
    },

    onWindowPointerUp(): void {
      if (!this.isDragging) {
        this.dragEnabled = false;
      }
    },

    onDragStart(event: DragEvent): void {
      if (!this.dragEnabled || this.disabled) {
        event.preventDefault();
        return;
      }

      this.isDragging = true;

      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", String(this.itemId));
      }

      this.$emit("drag-start", this.itemId);
    },

    onDragEnd(): void {
      this.dragEnabled = false;
      this.isDragging = false;
      this.isDragOver = false;

      this.$emit("drag-end", this.itemId);
    },

    onDragOver(event: DragEvent): void {
      if (this.disabled) {
        return;
      }

      event.preventDefault();
      this.isDragOver = true;

      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "move";
      }
    },

    onDragLeave(): void {
      this.isDragOver = false;
    },

    onDrop(event: DragEvent): void {
      if (this.disabled) {
        return;
      }

      event.preventDefault();
      this.isDragOver = false;

      const sourceId = event.dataTransfer?.getData("text/plain");

      if (!sourceId) {
        return;
      }

      this.$emit("drop-item", {
        sourceId,
        targetId: this.itemId
      });
    }
  }
});
</script>
